import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as IconAttachment } from "@/assets/svgs/data-dictionary-attachment.svg";
import { ReactComponent as IconDataDictionary } from "@/assets/svgs/data-dictionary-view-Details.svg";
import { ReactComponent as IconClose } from "@/assets/svgs/icon-close.svg";
import { ReactComponent as IconDown } from "@/assets/svgs/icon-down.svg";
import { ReactComponent as IconNext } from "@/assets/svgs/icon-next.svg";
import CardConfigure from "@/components/CardConfigure";
import Dropdown from "@/components/Dropdown2/index";
import Modal from "@/components/Modal2";
import MultiLineText from "@/components/MultiLineText/index.js";
import MultiSelectCheckbox2 from "@/components/MultiSelectCheckbox2/index";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import Search from '@/componentsDS/Search/index';
import useDataDictionary from "@/hooks/use-data-dictionary/index";
import useModal from "@/hooks/use-modal";
import usePage from "@/hooks/use-page";
import { useTradex } from "@/hooks/use-tradex/index.js";
import rem from "@/utils/rem.js";
import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { DataTableHeader, DataTableList, DisplayNestedRecursive, NoMessageUseCase, PageTitle, SearchInput, SubDataElementDetails, getAttachmentsStatus, getDataFieldType, getFieldCount, getPageLabel, openAttachment, prepareFetchParams } from "./data-dictionary.jsx";

const DataDictionaryPage = ({
	defaultUpdatedDataElement,
	defaultClassification,
	defaultSelectedTab,
	defaultSelectedField,
	defaultModal,
}) => {
	/*=================================================1
		General
	/================================================*/
	const [currentPage, setCurrentPage] = useState(1);
	const pageLimit = 10;

	const pageTitle = "Data Dictionary";
	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs:  [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Data Dictionary',
				link: `/data-dictionary`
			},
		],
	});

	const isTradex = useTradex();

	const [searchDataElement, setSearchDataElement] = useState("");
	const [searchDataFields, setSearchDataFields] = useState("");
	const [modalOpen, setModalOpen] = useState(defaultModal);
	const [classification, setClassification] = useState(defaultClassification);
	const [fieldSelected, setFieldSelected] = useState(defaultSelectedField);

	const [updatedDataElement, setUpdatedDataElement] = useState(
		defaultUpdatedDataElement
	);

	const [useCasesListing, setUseCasesListing] = useState([])

	const [selectedTab, setSelectedTab] = useState(defaultSelectedTab);
	const [selectedSubDataElementID, setSelectedSubDataElementID] =
		useState("");

	/*=================================================
	  API
	/================================================*/

	const {
		fetchDataDictionaryLists: fetchDataList,
		items: dictionaryList,
		fetchDataDictionaryDetails: fetchDataDetails,
		dataElementDetails: dictionaryDetails,
		allData: entireDictionaryData,
		loading,
		classificationsList,
		useCasesLists,
	} = useDataDictionary();

	function fetchData() {
		const params = prepareFetchParams(
			classification,
			useCasesListing,
			currentPage,
			pageLimit,
			searchDataElement
		);
		fetchDataList(params);
	}

	const reloadDataWithPage = () => {
		return () => setCurrentPage(1);
	};
	useEffect(reloadDataWithPage, [classification, useCasesListing, searchDataElement]);

	const reloadData = () => {
		const delayDebounceFn = setTimeout(fetchData, 500);
		return () => clearTimeout(delayDebounceFn);
	};
	useEffect(reloadData, [currentPage, classification, useCasesListing, searchDataElement]);

	useEffect(clearFilter, []);

	const handleUpdateDataElement = () => {
		setUpdatedDataElement(
			dictionaryDetails?.fields?.filter((check) => {
				return (
					(fieldSelected === 1 &&
						!check.isOptional &&
						check.title
							?.toLowerCase()
							.includes(searchDataFields.toLowerCase())) ||
					(fieldSelected === 2 &&
						check.isOptional &&
						check.title
							?.toLowerCase()
							.includes(searchDataFields.toLowerCase()))
				);
			})
		);
	};

	useEffect(handleUpdateDataElement, [
		searchDataFields,
		dictionaryDetails,
		fieldSelected,
	]);

	/*=================================================
	  Modals
	/================================================*/
	const {
		visible: visibleViewDetails,
		show: showViewDetails,
		hide: hideViewDetails,
	} = useModal();

	/*=================================================
	  Reset Filter && View Details
	/================================================*/

	function clearFilter() {
		setClassification("All (Default)");
		setSearchDataElement("");
		const newArr = useCasesListing.map((obj) => {
			return { ...obj, $selected: true };
		});
		setUseCasesListing(newArr);
	}

	function resetFields() {
		setSearchDataFields("");
		setSelectedTab(1);
		setFieldSelected(1);
		setModalOpen(true);
		showViewDetails();
	}

	async function openModal(id) {
		await fetchDataDetails(id).then((data) => {
			setUpdatedDataElement(data);
			resetFields();
		});
	}

	/*=================================================
	  Pagination
	/================================================*/

	function goToPrev() {
		return currentPage !== 1 && setCurrentPage((prev) => prev - 1);
	}

	function goToNext() {
		return currentPage !== entireDictionaryData?.pageCount && setCurrentPage((prev) => prev + 1);
	}

	useEffect(() => {
		if (currentPage > entireDictionaryData?.pageCount) {
			setCurrentPage(entireDictionaryData?.pageCount || 1);
		}
	}, [dictionaryList]);

	const APIDocumentation = () => {
		const list = dictionaryDetails?.useCases.map((item) => {
			return (
				<div className="modal-body__content" key={item}>
					<div className="modal-body__content-title">
						{item.useCaseTitle}
					</div>
					<div className="modal-body__content-body">
						{item.shortDescription}
					</div>
					<div className="modal-body__content-footer">
						<span
							className="modal-body__content-footer__link"
							onClick={() => {
								openAttachment(item.swaggerLink);
							}}
						>
							API Documentation
						</span>
						<IconAttachment />
					</div>
				</div>
			);
		});

		return (
			<>
				{list.length > 0 ? (
					<div className="data-table__table__body">{list}</div>
				) : (
					<NoMessageUseCase />
				)}
			</>
		);
	};

	const DataElementDetails = ({ detailsItem }) => {
		const list = detailsItem?.fields?.map((item) => {
			const id = item.id;
			return (
				<div className="data-dictionary__data-details__active-margin" key={id}>
					{item.id !== selectedSubDataElementID && (
						<div
							className="data-dictionary__fields-data-details data-dictionary__fields-sub__data-details__margin"
							onClick={() =>
								setSelectedSubDataElementID(item.id)
							}
						>
							<div className="data-dictionary__fields-sub__heading-header__class">
								{item.title} {!item.isOptional ? "*" : ""}
							</div>
							<div className="data-dictionary__fields-mandatory__fields">
								<IconNext />
							</div>
						</div>
					)}

					{item.id === selectedSubDataElementID && (
						<div
							className="data-dictionary__fields-data-details data-dictionary__fields-sub__data-details__margin"
							onClick={() => setSelectedSubDataElementID("")}
						>
							<div className="data-dictionary__fields-sub__heading-header__class">
								{item.title} {!item.isOptional ? "*" : ""}
							</div>
							<div className="data-dictionary__fields-mandatory__fields">
								<IconDown />
							</div>
						</div>
					)}

					{item.id === selectedSubDataElementID && (
						<SubDataElementDetails subDetailsItem={item} />
					)}
				</div>
			);
		});
		return (
			<>
				<div className="data-dictionary__fields-data">
					<div>
						<div className="data-dictionary__fields-data-header">
							<MultiLineText>{detailsItem?.description}</MultiLineText>
						</div>
						<div className="data-dictionary__fields-data-details">
							<div>
								<div className="data-dictionary__fields-data-details_header">
									Applicable Standard Name
								</div>
								<div className="data-dictionary__fields-data-details_data">
									<MultiLineText>{detailsItem?.applicableStandardName}</MultiLineText>
								</div>
							</div>
							<div>
								<div className="data-dictionary__fields-data-details_header">
									Data Field Type
								</div>
								<div className="data-dictionary__fields-data-details_data">
									<MultiLineText>{getDataFieldType(detailsItem)}</MultiLineText>
								</div>
							</div>
						</div>
						<div>
							<div className="data-dictionary__fields-data-details_header">
								Data Format
							</div>
							<div className="data-dictionary__fields-data-details_data">
								<MultiLineText>{detailsItem?.dataFormat}</MultiLineText>
							</div>
						</div>
					</div>
				</div>
				{list?.length > 0 ? (
					<>
						<div className="data-dictionary__fields-data-details data-dictionary__data-details__margin">
							<div className="data-dictionary__fields-sub__heading">
								Sub Data Fields
							</div>
							<div className="data-dictionary__fields-mandatory__field">
								* Mandatory Field
							</div>
						</div>
						{list}
					</>
				) : (
					""
				)}
			</>
		);
	};

	DataElementDetails.propTypes = {
		detailsItem: PropTypes.shape({
			description: PropTypes.string,
			applicableStandardName: PropTypes.string,
			dataFormat: PropTypes.string,
			fields: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
					title: PropTypes.string,
					isOptional: PropTypes.bool,
				})
			),
		}),
	};

	DataElementDetails.defaultProps = {
		detailsItem: {
			description: '',
			applicableStandardName: '',
			dataFormat: '',
			fields: [],
		}
	};

	function handlSubmitClicked() {
		return (currentPage === 1 && classification === "All (Default)"
			&& !searchDataElement
			&& (useCasesListing.length === 0 || JSON.stringify(useCasesListing) === JSON.stringify(useCasesLists))
		)
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			{PageTitle(pageTitle)}

			<hr />
			<div
				id="data-update-btn"
				onClick={handleUpdateDataElement}
				style={{
					display: "none",
				}}
			/>
			<div
				id="fetch-data-btn"
				onClick={() => { fetchData(); reloadData() }}
				style={{
					display: "none",
				}}
			/>

			<div
				id="open-modal-btn"
				onClick={() => { openModal(""); resetFields() }}
				style={{
					display: "none",
				}}
			/>
			<div className="overview">
				<div className="overview_filter">
					<div className="overview_filter__cols">
						{
							isTradex && (
								<div className="overview_filter__col">
									<div className="overview_filter__col__top overview_filter__col__title">
										Use Case
									</div>
									<div className="overview_filter__col__bottom overview_filter__col__bottom--background overview_filter__col__width">
										<MultiSelectCheckbox2
											items={(useCasesListing?.length > 0 ? useCasesListing : useCasesLists)}
											onChangeFunc={setUseCasesListing}
										/>
									</div>
								</div>
							)
						}
						<div className="overview_filter__col input-field">
							<div className="overview_filter__col__top overview_filter__col__title">
								Classification
							</div>
							<div className="overview_filter__col__top overview_filter__col__bottom--background overview_filter__col__width">
								<Dropdown
									full
									text={"All (Default)"}
									options={classificationsList}
									value={classification}
									submit={(data) => {
										setClassification(data);
									}}
								/>
							</div>
						</div>

						<div className="overview_filter__col">
							<div className="overview_filter__col__top">
								&nbsp;
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__top-background">
								<button
									id="clear-filters"
									type="button"
									className="overview_filter__no-border"
									onClick={() => clearFilter()}
									disabled={handlSubmitClicked()}
								>
									Clear filters
								</button>

							</div>
						</div>
					</div>
				</div>
				<div className="overview__content">
					<div className="data-table__content">
						<div className="data-table__content__cols">
							<div className="data-table__content__col">
								<div className="data-table__table">
									<SearchInput
										searchDataElement={searchDataElement}
										setSearchDataElement={
											setSearchDataElement
										}
										modalOpen={modalOpen}
									/>
									<DataTableHeader />

									<DataTableList
										data={dictionaryList}
										onClick={(item) =>
											openModal(item.dataElementId)
										}
										currentPage={currentPage}
										setCurrentPage={setCurrentPage}
										entireDictionaryData={
											entireDictionaryData
										}
										goToPrev={goToPrev}
										goToNext={goToNext}
										pageLimit={pageLimit}
									/>

									{dictionaryList?.length > 0 && (
										<div className="data-table__table__footer">
											<div className="data-table__table__footer__row">
												<div className="data-table__table__footer__row__left">
													<div className="data-table__table__pagination">
														<input
															data-testid="pagination-input"
															className="data-table__table__pagination__page"
															type="number"
															id="handleCurrentInput"
															value={currentPage}
															onChange={(e) =>
																setCurrentPage(
																	e.target
																		.value ===
																		""
																		? 1
																		: parseInt(
																			e
																				.target
																				.value
																		)
																)
															}
															min="1"
														/>
														<span className="data-table__table__pagination__page__meta">
															{" "}
															of{" "}
															{entireDictionaryData?.pageCount ||
																1}{" "}
															pages{" "}
														</span>
														<button
															data-testid="pagination-prev"
															className="data-table__table__pagination__prev"
															id="goToPrev"
															onClick={() =>
																goToPrev()
															}
															disabled={
																currentPage ===
																1
															}
														>
															<IconChevronDown />
														</button>
														<button
															data-testid="pagination-next"
															className="data-table__table__pagination__next"
															id="goToNext"
															onClick={() =>
																goToNext()
															}
															disabled={
																currentPage ===
																entireDictionaryData?.pageCount
															}
														>
															<IconChevronDown />
														</button>
													</div>
												</div>
												<div className="data-table__table__footer__row__right">
													<div className="data-table__table__meta">
														Showing{" "}
														{getPageLabel(
															entireDictionaryData?.count,
															pageLimit,
															currentPage - 1
														)}{" "}
														of{" "}
														{entireDictionaryData?.count ||
															0}{" "}
														entries
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<Modal visible={visibleViewDetails} close={() => { setModalOpen(false); hideViewDetails(); }}>
				<CardConfigure
					style={{
						maxWidth: rem(420),
						minWidth: rem(420),
						position: "relative",
						borderRadius: "0",
					}}
					type="data-dictionary"
				>
					<CardConfigure.Header>
						<CardConfigure.Header.Icon type="false">
							<div className="DS__icon-svg">
								<IconDataDictionary />
							</div>
						</CardConfigure.Header.Icon>
						<CardConfigure.Header.Close
							id="close-modal-btn"
							onClick={() => {
								hideViewDetails();
								setModalOpen(false);
							}}
						>
							<IconClose width={16} height={16} />
						</CardConfigure.Header.Close>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="modal-head__title">
								{dictionaryDetails?.name}
							</div>
							<div className="modal-head__sub-title">
								<div>
									{dictionaryDetails?.type === "DOCUMENT"
										? "Document"
										: "Event"}
								</div>
								<div>
									{getAttachmentsStatus(
										dictionaryDetails?.attachmentRequired
									)}
								</div>
							</div>
							<div className="modal-body__description">
								{dictionaryDetails?.desc}
							</div>

							{/* Div Tabs */}
							<div className="modal-body__selection-tab">
								<div
									className={
										selectedTab === 1
											? "modal-body__selected__tab"
											: "modal-body__tab"
									}
									onClick={() => setSelectedTab(1)}
								>
									Data Fields (
									{dictionaryDetails?.fields?.length})
								</div>
								{
									isTradex &&
										<div
											className={
												selectedTab === 2
													? "modal-body__selected__tab"
													: "modal-body__tab"
											}
											onClick={() => setSelectedTab(2)}
										>
											Applicable Use Cases
										</div>
								}
							</div>
							{selectedTab === 1 && (
								<>
									<div className="data-table__table__display">
										<div style={{ flex: 1 }}>
											<Search
												value={searchDataFields}
												placeholder="Search by field names"
												onChange={(e) => setSearchDataFields(e?.target.value)}
												onCancel={() => setSearchDataFields('')}
											/>
										</div>
									</div>
									<div className="data-dictionary__fields-display">
										<div
											className={
												fieldSelected === 1
													? "data-dictionary__details-display__active"
													: "data-dictionary__details-display"
											}
											onClick={() => setFieldSelected(1)}
										>
											Mandatory{" "}
											{!searchDataFields &&
												" (" +
												getFieldCount(
													dictionaryDetails,
													false
												) +
												")"}
										</div>
										<div
											className={
												fieldSelected === 2
													? "data-dictionary__details-display__active"
													: "data-dictionary__details-display"
											}
											onClick={() => setFieldSelected(2)}
										>
											Optional{" "}
											{!searchDataFields &&
												" (" +
												getFieldCount(
													dictionaryDetails,
													true
												) +
												")"}
										</div>
									</div>
									{fieldSelected === 1 && (
										<DisplayNestedRecursive data={updatedDataElement} isRoot={true} type={false} level={0} />
									)}
									{fieldSelected === 2 && (
										<DisplayNestedRecursive data={updatedDataElement} type={true} level={0} />
									)}
								</>
							)}
							{selectedTab === 2 && (
								<APIDocumentation />
							)}
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
				</CardConfigure>
			</Modal>

			{loading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src="/loading.gif" />
						</div>
					</div>,
					document.body
				)}
		</>
	);
};

DataDictionaryPage.propTypes = {
	defaultUpdatedDataElement: PropTypes.arrayOf(PropTypes.object),
	defaultClassification: PropTypes.string,
	defaultSelectedTab: PropTypes.number,
	defaultSelectedField: PropTypes.number,
	defaultModal: PropTypes.bool
}

DataDictionaryPage.defaultProps = {
	defaultUpdatedDataElement: [],
	defaultClassification: "All (Default)",
	defaultSelectedTab: 1,
	defaultSelectedField: 1,
	defaultModal: false
}


export default DataDictionaryPage;
